/**
 * Support widget — the snake_case → camelCase boundary.
 *
 * Keeping the conversion in a single function is what lets every component, selector and
 * spec stay in its own repo's convention without leaking an unrelated backend
 * serialization choice. Every field is read defensively: `isSupportApiResponse` only
 * vouches for `state` and `channels`, so everything else may still be missing.
 */
import type { SupportApiResponse, SupportAvailability, SupportChatProvider } from "./api.types"
import { SUPPORT_DEFAULT_TIMER_SECONDS } from "./constants"

/** The payload as the apps see it. */
export interface SupportState {
  state: SupportAvailability
  isOpen: boolean
  /** When the answer stops being true. Absent when the backend sends `null`. */
  validUntil?: string
  channels: {
    chat: { enabled: boolean; provider: SupportChatProvider }
    call: { enabled: boolean; phone: string }
    contactForm: { enabled: boolean }
  }
  schedule: {
    timezone: string
    weekdays: number[]
    opensAt: string
    closesAt: string
  }
  widget: {
    enabled: boolean
    timerSeconds: number
    showOutsideWorkingHours: boolean
  }
  flags: {
    useSalesforceChat: boolean
    useSalesforceChatNew: boolean
    useSalesforceContactUs: boolean
  }
}

export function toSupportState(raw: SupportApiResponse): SupportState {
  return {
    state: raw.state,
    isOpen: Boolean(raw.is_open),
    validUntil: raw.valid_until || undefined,
    channels: {
      chat: {
        enabled: Boolean(raw.channels?.chat?.enabled),
        provider: raw.channels?.chat?.provider || "",
      },
      call: {
        enabled: Boolean(raw.channels?.call?.enabled),
        phone: raw.channels?.call?.phone || "",
      },
      contactForm: { enabled: Boolean(raw.channels?.contact_form?.enabled) },
    },
    schedule: {
      timezone: raw.schedule?.timezone || "",
      weekdays: raw.schedule?.weekdays || [],
      opensAt: raw.schedule?.opens_at || "",
      closesAt: raw.schedule?.closes_at || "",
    },
    widget: {
      enabled: Boolean(raw.widget?.enabled),
      timerSeconds: raw.widget?.timer_seconds || SUPPORT_DEFAULT_TIMER_SECONDS,
      showOutsideWorkingHours: Boolean(raw.widget?.show_outside_working_hours),
    },
    flags: {
      useSalesforceChat: Boolean(raw.flags?.use_salesforce_chat),
      useSalesforceChatNew: Boolean(raw.flags?.use_salesforce_chat_new),
      useSalesforceContactUs: Boolean(raw.flags?.use_salesforce_contact_us),
    },
  }
}

/**
 * The only availability check any surface should make. `offline` and `hidden` are both
 * "do not offer a live channel"; what separates them is whether an entry point renders
 * at all, which is the surface's own decision.
 */
export function isAvailable(state: SupportAvailability): boolean {
  return state === "available"
}
